// src/pages/admin/ThemeSettings.jsx
import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Button,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Fade,
  Slide,
  Chip,
} from "@mui/material";
import {
  ColorLens,
  Restore,
  Close,
  Check,
  DarkMode,
  LightMode,
  Palette,
  Brush,
  Done,
  Refresh,
} from "@mui/icons-material";
import { useThemeContext } from "../../context/ThemeContext";

const ThemeSettings = () => {
  const { colors, updateColors } = useThemeContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [openPicker, setOpenPicker] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [tempColor, setTempColor] = useState("#ffffff");
  const [selectedColor, setSelectedColor] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const isDark = colors?.themeMode === "dark";
  const primaryColor = colors?.primaryColor || "#d32f2f";

  const themeRows = [
    {
      label: "Primary Color",
      key: "primaryColor",
      value: colors.primaryColor,
      icon: <Palette />,
    },
    {
      label: "Secondary Color",
      key: "secondaryColor",
      value: colors.secondaryColor,
      icon: <Brush />,
    },
    {
      label: "Background Color",
      key: "backgroundColor",
      value: colors.backgroundColor,
      icon: <ColorLens />,
    },
    {
      label: "Card / Paper Color",
      key: "paperColor",
      value: colors.paperColor,
      icon: <ColorLens />,
    },
    {
      label: "Text Primary",
      key: "textPrimary",
      value: colors.textPrimary,
      icon: <ColorLens />,
    },
    {
      label: "Text Secondary",
      key: "textSecondary",
      value: colors.textSecondary,
      icon: <ColorLens />,
    },
    {
      label: "Error Color",
      key: "errorColor",
      value: colors.errorColor,
      icon: <ColorLens />,
    },
    {
      label: "Warning Color",
      key: "warningColor",
      value: colors.warningColor,
      icon: <ColorLens />,
    },
    {
      label: "Info Color",
      key: "infoColor",
      value: colors.infoColor,
      icon: <ColorLens />,
    },
    {
      label: "Success Color",
      key: "successColor",
      value: colors.successColor,
      icon: <ColorLens />,
    },
  ];

  const colorPalettes = [
    { name: "Red", colors: ["#d32f2f", "#f44336", "#ef5350", "#e57373"] },
    { name: "Blue", colors: ["#1976d2", "#2196f3", "#42a5f5", "#64b5f6"] },
    { name: "Green", colors: ["#2e7d32", "#4caf50", "#66bb6a", "#81c784"] },
    { name: "Purple", colors: ["#7b1fa2", "#9c27b0", "#ab47bc", "#ce93d8"] },
    { name: "Orange", colors: ["#e65100", "#ff9800", "#ffa726", "#ffb74d"] },
    { name: "Teal", colors: ["#00695c", "#009688", "#26a69a", "#4db6ac"] },
    { name: "Pink", colors: ["#880e4f", "#e91e63", "#ec407a", "#f06292"] },
    { name: "Gray", colors: ["#424242", "#757575", "#9e9e9e", "#bdbdbd"] },
  ];

  const handleOpenPicker = (key, value) => {
    setSelectedKey(key);
    setTempColor(value);
    setSelectedColor(value);
    setOpenPicker(true);
  };

  const handleSaveColor = () => {
    updateColors({ [selectedKey]: tempColor });
    setOpenPicker(false);
    showSuccessMessage(
      `${selectedKey.replace(/([A-Z])/g, " $1").trim()} updated successfully!`,
    );
  };

  const handleQuickColorSelect = (color) => {
    setTempColor(color);
    setSelectedColor(color);
  };

  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleReset = () => {
    if (window.confirm("Reset all theme settings to defaults?")) {
      updateColors({
        primaryColor: "#d32f2f",
        secondaryColor: "#1976d2",
        backgroundColor: "#f5f5f5",
        paperColor: "#ffffff",
        textPrimary: "#212121",
        textSecondary: "#757575",
        errorColor: "#f44336",
        warningColor: "#ff9800",
        infoColor: "#2196f3",
        successColor: "#4caf50",
        themeMode: "light",
      });
      showSuccessMessage("Theme reset to defaults!");
    }
  };

  const handleToggleDarkMode = (checked) => {
    updateColors({ themeMode: checked ? "dark" : "light" });
    showSuccessMessage(checked ? "Dark mode enabled!" : "Light mode enabled!");
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2, md: 3 },
        pb: { xs: 12, sm: 10, md: 8 },
        minHeight: "100vh",
      }}
    >
      {successMessage && (
        <Slide direction="down" in={!!successMessage}>
          <Alert
            severity="success"
            icon={<Check />}
            sx={{
              mb: 2,
              borderRadius: 2,
              position: "sticky",
              top: { xs: 70, sm: 80 },
              zIndex: 10,
            }}
            action={
              <IconButton
                color="inherit"
                size="small"
                onClick={() => setSuccessMessage(null)}
              >
                <Close fontSize="small" />
              </IconButton>
            }
          >
            {successMessage}
          </Alert>
        </Slide>
      )}

      <Paper
        sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: { xs: 2, sm: 3 } }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Palette
                sx={{ color: primaryColor, fontSize: { xs: 24, sm: 28 } }}
              />
              <Typography
                variant={isMobile ? "h6" : "h5"}
                fontWeight="bold"
                sx={{ fontSize: { xs: "1.1rem", sm: "1.5rem" } }}
              >
                Theme & Color Settings
              </Typography>
              <Chip
                label={isDark ? "Dark Mode" : "Light Mode"}
                size="small"
                sx={{
                  ml: 1,
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(0,0,0,0.05)",
                  color: isDark ? "#fff" : "text.primary",
                }}
                icon={isDark ? <DarkMode /> : <LightMode />}
              />
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
            >
              Customize the look and feel of the platform
            </Typography>
          </Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            width={{ xs: "100%", sm: "auto" }}
          >
            <Button
              variant="outlined"
              color="error"
              startIcon={<Restore />}
              onClick={handleReset}
              fullWidth={isMobile}
              sx={{
                borderRadius: 2,
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
              }}
            >
              Reset to Defaults
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Alert
        severity="info"
        sx={{
          mb: 3,
          borderRadius: 2,
          fontSize: { xs: "0.8rem", sm: "0.875rem" },
        }}
        icon={<Refresh />}
      >
        Changes take effect immediately across the whole platform.
      </Alert>

      <Paper sx={{ borderRadius: { xs: 2, sm: 3 }, overflow: "hidden", mb: 3 }}>
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            bgcolor: "background.paper",
            gap: { xs: 1.5, sm: 0 },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            {isDark ? (
              <DarkMode sx={{ color: "#fff", fontSize: { xs: 28, sm: 32 } }} />
            ) : (
              <LightMode
                sx={{ color: "#f9a825", fontSize: { xs: 28, sm: 32 } }}
              />
            )}
            <Box>
              <Typography variant="subtitle1" fontWeight="600">
                Dark Mode
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isDark ? "Currently in dark mode" : "Currently in light mode"}
              </Typography>
            </Box>
          </Stack>
          <Switch
            checked={isDark}
            onChange={(e) => handleToggleDarkMode(e.target.checked)}
            color="primary"
          />
        </Box>
      </Paper>

      <Paper
        sx={{
          borderRadius: { xs: 2, sm: 3 },
          overflow: "hidden",
          mb: { xs: 2, sm: 0 },
        }}
      >
        <Box sx={{ overflowX: "auto" }}>
          <TableContainer>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead
                sx={{ bgcolor: isDark ? "rgba(255,255,255,0.05)" : "grey.50" }}
              >
                <TableRow>
                  <TableCell>
                    <Typography
                      variant={isMobile ? "caption" : "subtitle2"}
                      fontWeight="bold"
                    >
                      Setting
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant={isMobile ? "caption" : "subtitle2"}
                      fontWeight="bold"
                    >
                      Current Value
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant={isMobile ? "caption" : "subtitle2"}
                      fontWeight="bold"
                    >
                      Control
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {themeRows.map((row) => (
                  <TableRow
                    key={row.key}
                    hover
                    sx={{
                      "&:hover": {
                        bgcolor: isDark ? "rgba(255,255,255,0.05)" : undefined,
                      },
                    }}
                  >
                    <TableCell>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ minWidth: { xs: 80, sm: 120 } }}
                      >
                        <Box
                          sx={{
                            width: { xs: 14, sm: 18 },
                            height: { xs: 14, sm: 18 },
                            borderRadius: "50%",
                            bgcolor: row.value,
                            border: `1px solid ${isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)"}`,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant={isMobile ? "caption" : "body2"}
                          fontWeight="600"
                          sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                        >
                          {row.label}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant={isMobile ? "caption" : "body2"}
                        sx={{
                          fontFamily: "monospace",
                          color: "text.secondary",
                          fontSize: { xs: "0.65rem", sm: "0.875rem" },
                        }}
                      >
                        {row.value}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size={isMobile ? "small" : "medium"}
                        variant="outlined"
                        onClick={() => handleOpenPicker(row.key, row.value)}
                        sx={{
                          borderRadius: 2,
                          fontSize: { xs: "0.6rem", sm: "0.8rem" },
                          minWidth: { xs: 50, sm: 80 },
                          py: { xs: 0.5, sm: 1 },
                        }}
                      >
                        Change
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      <Box sx={{ height: { xs: 20, sm: 10 } }} />

      <Dialog
        open={openPicker}
        onClose={() => setOpenPicker(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 2, sm: 3 },
            m: { xs: 1, sm: 2 },
            width: "100%",
            maxWidth: { xs: "100%", sm: 500 },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider",
            pb: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <ColorLens sx={{ color: primaryColor }} />
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              Choose Color
            </Typography>
          </Stack>
          <IconButton onClick={() => setOpenPicker(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box
              sx={{
                width: { xs: 60, sm: 80 },
                height: { xs: 60, sm: 80 },
                borderRadius: "50%",
                bgcolor: tempColor,
                mx: "auto",
                border: "3px solid",
                borderColor: isDark
                  ? "rgba(255,255,255,0.2)"
                  : "rgba(0,0,0,0.1)",
                boxShadow: `0 4px 20px ${tempColor}40`,
                transition: "all 0.3s ease",
                mb: 2,
              }}
            />
            <input
              type="color"
              value={tempColor}
              onChange={(e) => setTempColor(e.target.value)}
              style={{
                width: { xs: 60, sm: 80 },
                height: { xs: 40, sm: 50 },
                cursor: "pointer",
                border: "none",
                padding: 0,
                borderRadius: 8,
                background: "transparent",
              }}
            />
            <Typography
              variant="body2"
              sx={{ fontFamily: "monospace", color: "text.secondary", mt: 1 }}
            >
              {tempColor}
            </Typography>
          </Box>
          <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>
            Quick Color Palettes
          </Typography>
          <Grid container spacing={1}>
            {colorPalettes.map((palette) => (
              <Grid item xs={12} key={palette.name}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: isDark ? "rgba(255,255,255,0.05)" : "grey.50",
                    "&:hover": {
                      bgcolor: isDark ? "rgba(255,255,255,0.08)" : "grey.100",
                    },
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    handleQuickColorSelect(palette.colors[0]);
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ minWidth: 50, fontWeight: 500 }}
                  >
                    {palette.name}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    {palette.colors.map((color) => (
                      <Box
                        key={color}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQuickColorSelect(color);
                        }}
                        sx={{
                          width: { xs: 20, sm: 24 },
                          height: { xs: 20, sm: 24 },
                          borderRadius: "50%",
                          bgcolor: color,
                          border:
                            selectedColor === color
                              ? `2px solid ${isDark ? "#fff" : "#000"}`
                              : "1px solid",
                          borderColor:
                            selectedColor === color
                              ? "primary.main"
                              : "rgba(0,0,0,0.1)",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": { transform: "scale(1.1)" },
                          position: "relative",
                        }}
                      >
                        {selectedColor === color && (
                          <Check
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              fontSize: { xs: 12, sm: 14 },
                              color: "#fff",
                              textShadow: "0 1px 3px rgba(0,0,0,0.3)",
                            }}
                          />
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Button
            onClick={() => setOpenPicker(false)}
            fullWidth={isMobile}
            sx={{ order: isMobile ? 2 : 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveColor}
            fullWidth={isMobile}
            startIcon={<Done />}
            sx={{ order: isMobile ? 1 : 2, borderRadius: 2 }}
          >
            Save Color
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ThemeSettings;
